import { findIdentityDocument } from "../../../../constants/identityDocuments";
import { COUNTRY_CODES } from "../../../../constants/countries";
import {
  PAIS_COSTA_RICA,
  revisarCodigoPostal,
  revisarOtrasSenas,
  ubicacionQueRecibeBC,
} from "./ubicacionDelReceptor";

/**
 * Lo que Hacienda va a rechazar, dicho antes de enviarlo.
 *
 * El problema que resuelve: cuando el comprobante se rechaza al timbrar, la
 * factura se quema y el cajero empieza de cero. El motivo que devuelve Business
 * Central sirve para corregir, pero no para prevenir. Esto se ejecuta **antes de
 * que nada salga del terminal**, así que el documento local sigue vivo: se
 * arregla el dato y se reintenta.
 *
 * ## Por qué se puede comprobar de antemano
 *
 * Hacienda valida en tres niveles (Anexos v4.4, pág. 17): **1** la estructura,
 * **2** el formato de cada campo, y **3** que el dato exista en sus padrones.
 * Los niveles 1 y 2 son deterministas y no necesitan red: se comprueban aquí
 * enteros. El nivel 3 --que una cédula bien formada exista de verdad-- no se
 * puede saber sin preguntarle a Hacienda, y no se finge saberlo.
 *
 * ## De dónde sale cada regla
 *
 * Del esquema oficial vigente, `FacturaElectronica_V4.4.xsd` y
 * `TiqueteElectronico_V4.4.xsd` (hacienda.go.cr, revisión del 22/04/2026), y de
 * los Anexos cuando la regla no está en el esquema sino en el validador --que es
 * lo que hace que el XSD las deje pasar y Hacienda las rechace--. Cada
 * comprobación dice de cuál de los dos viene.
 *
 * > **Aviso para quien actualice esto:** hay dos copias oficiales en circulación
 * > y no son iguales. La de `atv.hacienda.go.cr` está desactualizada. La buena es
 * > `www.hacienda.go.cr/docs/`. En la vigente la cédula jurídica pasó a ser
 * > alfanumérica de 10 caracteres, y la clave admite letras.
 */

/** Qué tan grave es lo que se encontró. */
export const GRAVEDAD = {
  /** Hacienda lo rechaza seguro. No dejarlo salir. */
  RECHAZA: "rechaza",
  /** Puede pasar, pero conviene mirarlo. */
  REVISAR: "revisar",
};

/**
 * El formato de correo que exige Hacienda.
 *
 * Está en los Anexos (págs. 22 y 24), no en el esquema: el XSD no trae ningún
 * `pattern` para el correo, así que un correo mal formado pasa la validación de
 * estructura y lo rechaza Hacienda después. Literal del documento: «deberá
 * cumplir con el formato indicado, caso contrario se rechazará el comprobante».
 */
const CORREO = /^\s*(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,}))\s*$/;

const texto = (valor) => String(valor ?? "").trim();

const problema = (campo, aviso, gravedad = GRAVEDAD.RECHAZA) => ({ campo, aviso, gravedad });

/**
 * Cuántos decimales trae un número, tal como se escribió.
 *
 * Se mira la representación y no el valor: `1.0` y `1` valen lo mismo para
 * Hacienda, y lo que el esquema limita es la cantidad de decimales enviados.
 */
const decimalesDe = (valor) => {
  const [, parteDecimal = ""] = String(valor ?? "").split(".");
  return parteDecimal.replace(/0+$/, "").length;
};

/**
 * El teléfono, que es el que más rechaza.
 *
 * `NumTelefono` es **`xs:integer`** en el esquema, no una cadena. Cualquier cosa
 * que no sea un dígito --una diagonal separando dos números, un espacio, un
 * guion, un `+`, un paréntesis-- rompe la validación de estructura y el
 * comprobante no llega ni a evaluarse. Es el error más fácil de arrastrar,
 * porque en la ficha del cliente un teléfono con diagonal se ve perfectamente
 * normal.
 *
 * Longitud: los Anexos piden de 8 a 20 dígitos, con la excepción de los números
 * especiales --«por ejemplo, el 911»--. El esquema admite desde 3, así que la
 * regla estricta es la de los Anexos.
 */
export const revisarTelefono = (valor, campo = "Teléfono") => {
  const crudo = texto(valor);
  if (!crudo) return null; // Es opcional en factura y en tiquete.

  const soloDigitos = crudo.replace(/\D/g, "");

  if (/[/,;]/.test(crudo) || soloDigitos.length > 20) {
    return problema(
      campo,
      `«${crudo}» parece llevar más de un número. Hacienda admite uno solo, y sólo dígitos: deja el que quieras que viaje.`
    );
  }
  if (crudo !== soloDigitos) {
    return problema(
      campo,
      `«${crudo}» lleva caracteres que no son dígitos. Hacienda no admite espacios, guiones ni signos: quedaría «${soloDigitos}».`
    );
  }
  if (soloDigitos.length < 8) {
    return problema(campo, `«${crudo}» es corto. Hacienda pide al menos 8 dígitos.`);
  }
  return null;
};

/**
 * Lo que cabe del correo en el comprobante.
 *
 * Hacienda admite 160, pero el correo llega al comprobante por `E-Mail-FE`, que
 * en el borrador de Business Central mide 80 (`Sales Header`, campo 55205). Uno
 * más largo lo rechaza BC con `CORREO_FE_LARGO`, así que manda el tope de BC.
 * Acuerdo `contrato-ubicacion-y-correo-fe-cri-2026-09-13.md`, fila T8.
 */
export const TOPE_DEL_CORREO = 80;

/** El correo, contra el formato de los Anexos y contra lo que cabe. */
export const revisarCorreo = (valor, campo = "Correo electrónico") => {
  const crudo = texto(valor);
  if (!crudo) return null;

  if (crudo.length > TOPE_DEL_CORREO) {
    return problema(
      campo,
      `El correo pasa de ${TOPE_DEL_CORREO} caracteres, que es lo que cabe en el comprobante.`
    );
  }
  if (/[;,/]/.test(crudo)) {
    return problema(
      campo,
      `«${crudo}» parece llevar más de una dirección. En el comprobante viaja una sola.`
    );
  }
  if (!CORREO.test(crudo)) {
    return problema(campo, `«${crudo}» no tiene formato de correo. Hacienda lo rechaza.`);
  }
  return null;
};

/**
 * Los datos del comprador.
 *
 * En una **factura** el receptor es obligatorio con su identificación. En un
 * **tiquete** el nodo entero puede no ir --venta de mostrador sin datos--, pero
 * si se captura algo, el nombre pasa a ser obligatorio: es el error clásico de
 * apuntar la cédula y dejar el nombre en blanco.
 */
export const revisarComprador = ({ cliente, esTiquete }) => {
  const avisos = [];
  const nombre = texto(cliente?.Bill_to_Name ?? cliente?.name);
  const numero = texto(cliente?.VAT_Registration_No_ ?? cliente?.Numero_Identificacion_fiscal);
  const tipo = texto(cliente?.Tipo_Documento_SrI);
  const hayAlgoDelComprador = Boolean(nombre || numero || tipo);

  if (!esTiquete && !numero) {
    avisos.push(
      problema("Cliente", "Una factura tiene que identificar al comprador; un tiquete no.")
    );
  }

  // Nombre: 3 a 100 en el esquema, y obligatorio en cuanto el receptor existe.
  if (hayAlgoDelComprador && !nombre) {
    avisos.push(problema("Nombre", "Si se capturan datos del comprador, el nombre es obligatorio."));
  } else if (nombre && nombre.length < 3) {
    avisos.push(problema("Nombre", `«${nombre}» es muy corto: Hacienda pide al menos 3 caracteres.`));
  } else if (nombre.length > 100) {
    avisos.push(problema("Nombre", "El nombre pasa de 100 caracteres, que es el máximo del esquema."));
  }

  // La identificación, con las reglas que el terminal ya tiene declaradas.
  if (numero && tipo) {
    const documento = findIdentityDocument(COUNTRY_CODES.COSTARICA, tipo);
    if (documento?.regex && !documento.regex.test(numero)) {
      avisos.push(problema("Identificación", documento.message || `«${numero}» no tiene el formato de ${documento.label}.`));
    }
  }

  avisos.push(revisarCorreo(cliente?.E_Mail));
  avisos.push(revisarTelefono(cliente?.Telefono, "Teléfono"));
  avisos.push(revisarTelefono(cliente?.Mobil, "Celular"));

  // La dirección no se revisa aquí: `OtrasSenas` sale de la ficha del cliente, no
  // de la cabecera. Ver `revisarUbicacion`.

  return avisos.filter(Boolean);
};

/**
 * La ubicación del comprador: país, código postal y otras señas.
 *
 * Se revisa lo que Business Central va a tener al registrar --lo del documento
 * si viaja, lo de la ficha si no--, porque es lo que lee CertificaE 1.0.0.6:
 *
 * - **Sin país** falla el comprobante entero, lleve o no ubicación: CertificaE
 *   busca el país del cliente antes de decidir (`CU-CRBuidlXML.al:570`).
 * - **Factura y tiquete** llevan ubicación si el país es Costa Rica. El tiquete
 *   la omite sólo con `Enable Receiver Node`, y hoy ningún cliente lo tiene
 *   (medido el 13/09/2026 en TEST y PROD). Mientras el terminal no lo lea, se
 *   trata como apagado.
 * - **Nota de crédito**, siempre, sea cual sea el país (`CU-CRBuidlXML.al:362`).
 *
 * Acuerdo `contrato-ubicacion-y-correo-fe-cri-2026-09-13.md`, fila T5.
 *
 * @param {object} args
 * @param {object} args.documento  La cabecera local.
 * @param {object} [args.ficha]    El cliente local.
 * @param {boolean} [args.esNotaDeCredito]
 */
export const revisarUbicacion = ({ documento, ficha, esNotaDeCredito = false }) => {
  const { codigoPostal, pais, direccion, direccion2 } = ubicacionQueRecibeBC(documento, ficha);

  if (!pais) {
    return [
      problema(
        "País",
        "El cliente no tiene país en su ficha, y sin país el comprobante no se puede armar. Corrígelo en la ficha del cliente."
      ),
    ];
  }

  const esDeCostaRica = pais === PAIS_COSTA_RICA;
  if (!esDeCostaRica && !esNotaDeCredito) return [];

  const avisos = [];

  const delCodigo = revisarCodigoPostal(codigoPostal, PAIS_COSTA_RICA);
  if (delCodigo) {
    avisos.push(
      problema(
        "Código postal",
        esDeCostaRica
          ? delCodigo
          : `La nota de crédito pide la ubicación de Costa Rica aunque el cliente sea de otro país (${pais}). ${delCodigo}`
      )
    );
  }

  const deLaDireccion = revisarOtrasSenas(direccion, direccion2);
  if (deLaDireccion) avisos.push(problema("Dirección", deLaDireccion));

  return avisos;
};

/**
 * Las líneas del documento.
 *
 * `Detalle` va de 3 a 200 caracteres, `Cantidad` admite hasta 3 decimales y
 * **no puede ser cero** --esto último no lo impone el esquema, lo rechaza
 * Hacienda--, y los importes admiten hasta 5 decimales.
 *
 * La cantidad se mira **en valor absoluto**, porque en una nota de crédito las
 * cantidades van en negativo: es el convenio del propio terminal, que las fuerza
 * con `-Math.abs()` (`InvoiceLines.js:1045`). Ese signo no llega nunca a
 * Hacienda --el importador de Business Central vuelve a negarlo, y las dos
 * inversiones se cancelan--, así que mirarlo aquí no comprueba nada de lo que
 * Hacienda va a ver.
 *
 * Mirar el signo hacía que **ninguna nota de crédito de Costa Rica saliera del
 * terminal**, con un aviso que además pedía lo contrario de lo que el propio
 * terminal acababa de escribir. Es el mismo defecto que bloqueó la primera nota
 * de crédito de Panamá el 18/09/2026: allí se corrigió y aquí se quedó, porque
 * los dos validadores son copias que no comparten código. Visto en CRI TEST el
 * 22/09/2026.
 */
export const revisarLineas = (lineas) => {
  const avisos = [];

  (lineas || []).forEach((linea, i) => {
    const donde = `Línea ${i + 1}`;
    const descripcion = texto(linea?.Description);
    const cantidad = Math.abs(Number(linea?.Quantity ?? 0));

    if (descripcion.length < 3) {
      avisos.push(
        problema(donde, `La descripción «${descripcion}» es muy corta: Hacienda pide 3 caracteres.`)
      );
    } else if (descripcion.length > 200) {
      avisos.push(problema(donde, "La descripción pasa de 200 caracteres."));
    }

    if (!(cantidad > 0)) {
      avisos.push(problema(donde, "La cantidad no puede ser cero."));
    } else if (decimalesDe(linea?.Quantity) > 3) {
      avisos.push(problema(donde, "La cantidad admite 3 decimales como mucho."));
    }

    if (decimalesDe(linea?.Unit_Price) > 5) {
      avisos.push(problema(donde, "El precio admite 5 decimales como mucho."));
    }
  });

  return avisos;
};

/** Un porcentaje de IVA comparable: dos decimales, que es como lo publica BC. */
const porcentaje = (valor) => Math.round(Number(valor ?? 0) * 100) / 100;

/**
 * El IVA que le toca al cliente, frente al que le tocaría a un cliente de mostrador.
 *
 * El caso que la originó: `VFR15-009039`, a un cliente `INT`, se registró al 0 %
 * --`INT` con `_GR_13` vale 0 % en BC-- y Hacienda la rechazó porque ese producto
 * tributa al 13 %. El tiquete `VFR15-009040` falló igual, así que bloquear por tipo
 * de identificación o sólo en factura no alcanzaba.
 *
 * **La referencia es el cliente por defecto de la tienda**, no «el IVA del
 * producto». Esa lectura tiene dos versiones en el terminal y ninguna sirve: la
 * fila (producto, "") vale 0 % en `_GR_13`, y contra ella saltaban 11.802 de
 * 11.812 clientes en DEV. El de mostrador es `NACBIEN`, y contra él sólo difieren
 * los 38 `INT` y dos grupos de producto de `NACSERV`: justo lo que Hacienda rechaza.
 *
 * Los dos IVA se resuelven con la misma cascada, la de `getVatByProductAndClient`,
 * que es la que puso el IVA en la línea. Por eso llega inyectada: así se prueba sin
 * Dexie y esta hoja sigue sin tocar la base. La que la resuelve desde Dexie es
 * `revisarIvaDelDocumento`, en `ivaDelDocumento.js`.
 *
 * **Sin cliente por defecto no se bloquea**: sólo 2 de 25 tiendas lo tenían en DEV
 * y 1 de 25 en PROD. Se devuelve el indicador para que quien llama lo anote.
 *
 * Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, fila H3 · TPV.
 *
 * @param {object} args
 * @param {Array} args.lineas  Las líneas del documento; de cada una se usa `No`.
 * @param {object} [args.cliente]  La ficha del cliente de la venta.
 * @param {object} [args.clientePorDefecto]  La ficha del cliente por defecto de la tienda.
 * @param {(no: string) => Promise<object|null>} [args.buscarProducto]  Sin ella, la
 *   línea hace de producto.
 * @param {(producto: object, cliente: object) => Promise<{VAT_Percent: number}>} args.buscarIva
 * @param {boolean} [args.esNotaDeCredito]
 * @param {string} [args.countryCode]  Si se omite, se asume Costa Rica, como en
 *   `revisarParaHacienda`.
 * @returns {Promise<{avisos: Array<{campo: string, aviso: string, gravedad: string}>, sinClientePorDefecto: boolean}>}
 */
export const revisarIvaDelCliente = async ({
  lineas,
  cliente,
  clientePorDefecto,
  buscarProducto,
  buscarIva,
  esNotaDeCredito,
  countryCode,
} = {}) => {
  const nada = { avisos: [], sinClientePorDefecto: false };

  if (countryCode !== undefined && countryCode !== COUNTRY_CODES.COSTARICA) return nada;
  // La nota de crédito hereda el IVA de la factura que abona: no se elige cliente.
  if (esNotaDeCredito) return nada;
  if (!clientePorDefecto) return { ...nada, sinClientePorDefecto: true };
  if (!cliente || typeof buscarIva !== "function") return nada;

  // El propio cliente de mostrador no tiene contra quién diferir.
  const noDelCliente = texto(cliente.No);
  if (noDelCliente && noDelCliente === texto(clientePorDefecto.No)) return nada;

  const grupo = texto(cliente.VAT_Bus_Posting_Group) || "sin grupo";
  const avisos = [];

  for (const [i, linea] of (lineas || []).entries()) {
    const producto = buscarProducto ? await buscarProducto(linea?.No) : linea;
    if (!producto) continue;

    const [delCliente, deMostrador] = await Promise.all([
      buscarIva(producto, cliente),
      buscarIva(producto, clientePorDefecto),
    ]);
    const x = porcentaje(delCliente?.VAT_Percent);
    const y = porcentaje(deMostrador?.VAT_Percent);
    if (x === y) continue;

    avisos.push(
      problema(
        `Línea ${i + 1} (${texto(linea?.No) || texto(linea?.Description)})`,
        `el grupo de IVA del cliente (${grupo}) le aplica ${x} %, y a un cliente de mostrador ${y} %. ` +
          "Hacienda rechazaría el comprobante: elige otro cliente o pide que corrijan su grupo de IVA en Business Central."
      )
    );
  }

  return { avisos, sinClientePorDefecto: false };
};

/**
 * Todo junto: lo que hay que arreglar antes de que el documento salga.
 *
 * El país se comprueba **aquí dentro**, y no en quien llama.
 *
 * Estas reglas son las de Hacienda de Costa Rica. Corren desde el tronco de la
 * sincronización, por donde pasan los seis países, así que dejar la condición
 * fuera sería cuestión de tiempo que alguien añadiera un camino y bloqueara las
 * ventas de Ecuador con una regla costarricense. Sin país declarado no se
 * valida: callar es el error barato de los dos.
 *
 * @param {object} args
 * @param {object} args.cliente  Los datos del comprador, de la cabecera.
 * @param {object} [args.ficha]  El cliente local: de ahí toma BC lo que la cabecera no lleva.
 * @param {Array} args.lineas    Las líneas del documento.
 * @param {boolean} args.esTiquete
 * @param {boolean} [args.esNotaDeCredito]
 * @param {string} [args.countryCode] El país activo. Si se omite, se asume Costa Rica
 *   --quien llama sin país es una prueba de estas mismas reglas--.
 * @returns {Array<{campo: string, aviso: string, gravedad: string}>}
 */
export const revisarParaHacienda = ({
  cliente,
  ficha,
  lineas,
  esTiquete,
  esNotaDeCredito,
  countryCode,
} = {}) => {
  if (countryCode !== undefined && countryCode !== COUNTRY_CODES.COSTARICA) return [];

  return [
    ...revisarComprador({ cliente, esTiquete: Boolean(esTiquete) }),
    // Sin documento no hay ubicación que revisar.
    ...(cliente
      ? revisarUbicacion({ documento: cliente, ficha, esNotaDeCredito: Boolean(esNotaDeCredito) })
      : []),
    ...revisarLineas(lineas),
  ];
};

/** Sólo lo que Hacienda rechazaría seguro. */
export const loQueRechazaria = (avisos) =>
  (avisos || []).filter((a) => a?.gravedad === GRAVEDAD.RECHAZA);
